/* ── Shared constants for style editor dialogs ─────────────────────
 * Arrowhead options, lineweight presets and related helpers used by both
 * DimStyleEditorDialog and MultileaderStyleEditorDialog (and the main
 * App property grid).  Extracted to avoid circular imports.
 */

// ── Arrowhead option constants ───────────────────────────────────

// Arrowhead option canonical IDs. Display labels are translated at render
// time via t(); arrowBlockNameFromId / arrowIdFromBlockName round-trip
// through these canonical IDs so the DXF block name (e.g. "_CLOSEDBLANK")
// is stable across languages.
//
// AutoCAD parity: the DOM <option value="..."> is the stable English label
// (e.g. "Oblique", "Closed filled") — mirrors how AutoCAD command line
// accepts English internal names even when the UI is localized. Tests and
// automation use selectOption("Oblique") against this stable value, while
// the displayed text renders via t(arrowheadOptionLabelKeys[id]).
export const arrowheadOptionIds = [
  "closed_filled",
  "closed_blank",
  "open",
  "dot",
  "dot_blank",
  "arch_tick",
  "oblique",
  "open_30",
  "none",
] as const;
type ArrowheadOptionId = (typeof arrowheadOptionIds)[number];
export const arrowheadOptionLabelKeys: Record<ArrowheadOptionId, string> = {
  closed_filled: "arrowhead.closed_filled",
  closed_blank: "arrowhead.closed_blank",
  open: "arrowhead.open",
  dot: "arrowhead.dot",
  dot_blank: "arrowhead.dot_blank",
  arch_tick: "arrowhead.arch_tick",
  oblique: "arrowhead.oblique",
  open_30: "arrowhead.open_30",
  none: "arrowhead.none",
};
// Stable English labels used as <option value="..."> so test automation
// (Playwright selectOption) and the command line layer can reference
// arrowheads by a fixed identifier regardless of the active UI language.
export const arrowheadOptionEnglishLabels: Record<ArrowheadOptionId, string> = {
  closed_filled: "Closed filled",
  closed_blank: "Closed blank",
  open: "Open",
  dot: "Dot",
  dot_blank: "Dot blank",
  arch_tick: "Arch tick",
  oblique: "Oblique",
  open_30: "Open 30",
  none: "None",
};
export const arrowheadEnglishLabelToId: Record<string, ArrowheadOptionId> =
  Object.entries(arrowheadOptionEnglishLabels).reduce(
    (acc, [id, label]) => {
      acc[label] = id as ArrowheadOptionId;
      return acc;
    },
    {} as Record<string, ArrowheadOptionId>,
  );

// Map language-agnostic option ID to DXF block name (DIMBLK1 / DIMBLK2 value).
const arrowIdToBlockName: Record<ArrowheadOptionId, string> = {
  closed_filled: "",
  closed_blank: "_CLOSEDBLANK",
  open: "_OPEN",
  dot: "_DOT",
  dot_blank: "_DOTBLANK",
  arch_tick: "_ARCHTICK",
  oblique: "_OBLIQUE",
  open_30: "_OPEN30",
  none: "_NONE",
};
const blockNameToArrowId: Record<string, ArrowheadOptionId> = {
  "": "closed_filled",
  _CLOSEDBLANK: "closed_blank",
  _OPEN: "open",
  _DOT: "dot",
  _DOTBLANK: "dot_blank",
  _ARCHTICK: "arch_tick",
  _OBLIQUE: "oblique",
  _OPEN30: "open_30",
  _NONE: "none",
};

export function arrowBlockNameFromId(id: string): string {
  return arrowIdToBlockName[id as ArrowheadOptionId] ?? "";
}

export function arrowIdFromBlockName(blockName: string): ArrowheadOptionId {
  return blockNameToArrowId[blockName] ?? "closed_filled";
}

// ── Lineweight presets ───────────────────────────────────────────

// AutoCAD-standard lineweight presets. The `value` strings are the
// stable internal contract used in entity fields and CHPROP — labels
// are translated via t() at render time. The numeric values match the
// AutoCAD LWEIGHT command list (millimeters, with two decimals).
// "ByLayer" / "ByBlock" / "Default" mirror AutoCAD's special values.
export const LINEWEIGHT_PRESET_VALUES: readonly string[] = [
  "ByLayer",
  "ByBlock",
  "Default",
  "0.00",
  "0.05",
  "0.09",
  "0.13",
  "0.15",
  "0.18",
  "0.20",
  "0.25",
  "0.30",
  "0.35",
  "0.40",
  "0.50",
  "0.53",
  "0.60",
  "0.70",
  "0.80",
  "0.90",
  "1.00",
  "1.06",
  "1.20",
  "1.40",
  "1.58",
  "2.00",
  "2.11",
];

// Match a numeric lineweight value (with optional " mm" suffix or comma
// decimal separator) so legacy entities written with formats like
// "0,25 mm" / "0.25 mm" / "0.25" all collapse to a single canonical
// preset entry instead of cluttering the dropdown.
export function normalizeLineweightValue(raw: string): string {
  if (!raw) return "ByLayer";
  const trimmed = raw.trim();
  if (trimmed === "ByLayer" || trimmed === "ByBlock" || trimmed === "Default") {
    return trimmed;
  }
  const numericMatch = trimmed
    .replace(/\s*mm\s*$/iu, "")
    .replace(/,/u, ".")
    .match(/^-?\d+(?:\.\d+)?$/u);
  if (numericMatch) {
    const numeric = Number(numericMatch[0]);
    if (Number.isFinite(numeric)) {
      return numeric.toFixed(2);
    }
  }
  return trimmed;
}
